// ============================================================================
// chatResume — which thread the Flow launcher reopens on mount
// ============================================================================
// The launcher used to pass no conversation id at all, so every remount opened
// a blank thread with the athlete's real one sitting in the list beside it.
// iOS has always resumed (AIChatViewModel.autoLoadRecent). This is the webapp
// half, with a bound iOS does not have — the launcher is mounted on every page,
// so it reopens far more often than an iOS sheet does, and silently reappearing
// inside a three-week-old thread reads as a bug rather than a convenience.
//
// The window is measured, not guessed: 489 gaps between consecutive turns in
// the same conversation, from 110 multi-turn conversations over the 14 days to
// 2026-09-07 (Loki `stream_closed` records; conversation ids and timestamps
// only). 77.7% of continuations came within 5 minutes, 88.8% within an hour,
// 95.9% within 24 hours — and then the curve flattens: 48h buys another 1.9pp
// and a full week only 3.9pp. 24h sits on the knee, so it keeps essentially
// every real continuation while a thread the athlete has genuinely moved on
// from stays closed.

import type { Conversation } from '@/services/api/chat'

/** How long a thread stays resumable after its last turn. See above. */
export const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * The conversation the launcher should reopen, or null to open blank.
 *
 * Picks the most recently updated non-archived conversation, and only if its
 * last turn falls inside RESUME_WINDOW_MS.
 */
export function resumableConversationId(
  conversations: Conversation[] | undefined,
  now: number = Date.now(),
): number | null {
  if (!conversations?.length) return null

  let best: Conversation | null = null
  let bestAt = -Infinity

  for (const c of conversations) {
    if (c.is_archived) continue
    // An unparseable timestamp gives NaN, and every NaN comparison is false —
    // so the `>` below already skips it. No separate guard needed.
    const updatedAt = Date.parse(c.updated_at)
    if (updatedAt > bestAt) {
      bestAt = updatedAt
      best = c
    }
  }

  if (!best) return null
  // A clock skewed into the future must not make a thread un-resumable.
  return now - bestAt <= RESUME_WINDOW_MS ? best.id : null
}
